/*
 * Content data for the public /results page. Presentation-free: components
 * render whatever is here, subject to the consent gate.
 *
 * consentOnFile: an entry renders ONLY when this is exactly `true`. The gate is
 * enforced AGAIN inside the rendering components (ResultCard / ResultVideoTile
 * both return null without it), because these entries are minors' faces, full
 * names and scores — never flip it without written consent on file.
 *
 * The 13 resultCards entries AND the 13 resultVideos entries below all carry
 * `consentOnFile: true` — owner-confirmed, consent collected at enrolment
 * for each of these named students (two separate rosters, not the same 13
 * people in both lists).
 *
 * The two fixtures marked below stay `false` forever and must never be
 * flipped: they are the only live proof that the gate still discriminates
 * rather than the section merely being empty. Keep at least one unconsented
 * fixture in each list forever.
 *
 * Schema note (poster-kit alignment): each card carries `slug` alongside the
 * pre-existing `id` (same value) so a record can be handed to the separate
 * inspire-poster-kit tool by matching field name where one already existed.
 * `subjects` ({name, score}) already carries both the subject list AND the
 * marks in one field — a separate `focus`/`scores` field was considered and
 * rejected: one concept, one field. `score` may be omitted on a subject entry
 * when only the subject name is known (no marks given on the source poster).
 * `outcome` is now OPTIONAL: when absent, the rendered admission line is
 * composed from `institution` + `programme` at render time; when present, it
 * overrides that composition (used for multi-institution or no-institution
 * outcomes — see ResultCard.js). `metric` is a single headline figure used
 * instead of subject marks (e.g. a percentile) — null when scores carry the
 * story. `featured` marks the 6 cards shown on the homepage rail
 * (components/results/ResultsRail.js) — that component owns the deliberate
 * display ORDER (a curated spread, not prestige-sorted); this field only
 * controls membership.
 *
 * Headshots: interim crops at public/assets/achievers/[slug].webp, sourced
 * from achievers-headshots-v2.zip (cropped square photos only — no branding
 * baked in). Do NOT source headshots from inspireresultsposters.zip or any
 * file named like "arnav-gulati-imperial.jpg" / "ekkam-thapar.jpg" — those
 * are the full original Inspire-branded posters with "Inspire Academics" and
 * "inspireacademics.in" printed into the pixels; they must never reach this
 * site. navya-mahendroo has no headshot file by design — renders the
 * initials avatar.
 *
 * Do NOT port the old Inspire-branded creatives from the old site — they
 * carry the old branding. Videos are YouTube-unlisted only (store the ID
 * here); no self-hosted .mp4, ever.
 */

// Homepage strip shows the first 4 (see LandingPage.js's homeStories slice);
// all 13 render on /results#stories. No caption/batchYear on any of these —
// these are students describing their experience, not tied to a subject or
// a result, so ResultVideoTile renders name only for this whole roster.
export const resultVideos = [
  {
    id: 'vidhi-aggarwal',
    slug: 'vidhi-aggarwal',
    studentName: 'Vidhi Aggarwal',
    batchYear: null,
    youtubeId: 'hcUvhSVVz9o',
    posterSrc: '/assets/stories/vidhi-aggarwal.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'rajit-ranjan',
    slug: 'rajit-ranjan',
    studentName: 'Rajit Ranjan',
    batchYear: null,
    youtubeId: 'DqE5M8QxixQ',
    posterSrc: '/assets/stories/rajit-ranjan.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'pia-jain',
    slug: 'pia-jain',
    studentName: 'Pia Jain',
    batchYear: null,
    youtubeId: '_Q9E8aJ4M5k',
    posterSrc: '/assets/stories/pia-jain.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'mahika-mahawar',
    slug: 'mahika-mahawar',
    studentName: 'Mahika Mahawar',
    batchYear: null,
    youtubeId: 'WNMOEfWMU5Q',
    posterSrc: '/assets/stories/mahika-mahawar.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'janya-gupta',
    slug: 'janya-gupta',
    studentName: 'Janya Gupta',
    batchYear: null,
    youtubeId: 'wsFNl3Srb2U',
    posterSrc: '/assets/stories/janya-gupta.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'darsh-jain',
    slug: 'darsh-jain',
    studentName: 'Darsh Jain',
    batchYear: null,
    youtubeId: 'uphFB3wymS0',
    posterSrc: '/assets/stories/darsh-jain.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'arohi-aggarwal',
    slug: 'arohi-aggarwal',
    studentName: 'Arohi Aggarwal',
    batchYear: null,
    youtubeId: 'DAvVWC2VI6s',
    posterSrc: '/assets/stories/arohi-aggarwal.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'anish-gupta',
    slug: 'anish-gupta',
    studentName: 'Anish Gupta',
    batchYear: null,
    youtubeId: 'ra_hKQcboZY',
    posterSrc: '/assets/stories/anish-gupta.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'angel-khokhar',
    slug: 'angel-khokhar',
    studentName: 'Angel Khokhar',
    batchYear: null,
    youtubeId: 'X6o9Nru8a0Q',
    posterSrc: '/assets/stories/angel-khokhar.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'ahad-ahmed',
    slug: 'ahad-ahmed',
    studentName: 'Ahad Ahmed',
    batchYear: null,
    youtubeId: 'yFwFCAW1EpU',
    posterSrc: '/assets/stories/ahad-ahmed.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'abhiraj-singh',
    slug: 'abhiraj-singh',
    studentName: 'Abhiraj Singh',
    batchYear: null,
    youtubeId: 'Ui19Ngb-S-Q',
    posterSrc: '/assets/stories/abhiraj-singh.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'aarav-chandel',
    slug: 'aarav-chandel',
    studentName: 'Aarav Chandel',
    batchYear: null,
    youtubeId: 'LfFctol4utQ',
    posterSrc: '/assets/stories/aarav-chandel.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    id: 'aadya-pathak',
    slug: 'aadya-pathak',
    studentName: 'Aadya Pathak',
    batchYear: null,
    youtubeId: 'hJ_qtiM91Tg',
    posterSrc: '/assets/stories/aadya-pathak.webp',
    caption: null,
    consentOnFile: true,
  },
  {
    // Deliberately consent-less seed: proves the gate. Must NEVER render.
    id: 'video-placeholder-no-consent',
    slug: 'video-placeholder-no-consent',
    studentName: '[PLACEHOLDER_NO_CONSENT_VIDEO]',
    batchYear: '[PLACEHOLDER_BATCH_YEAR]',
    youtubeId: '[PLACEHOLDER_YOUTUBE_ID]',
    posterSrc: null,
    caption: '[PLACEHOLDER_CAPTION]',
    consentOnFile: false,
  },
];

export const resultCards = [
  {
    id: 'vihaan-chopra',
    slug: 'vihaan-chopra',
    studentName: 'Vihaan Chopra',
    posterSrc: '/assets/posters/vihaan-chopra.webp',
    photoSrc: null,
    board: 'CBSE Class 10',
    batchYear: '2025-26',
    subjects: [
      { name: 'Mathematics', score: '100' },
      { name: 'Science', score: '97' },
    ],
    metric: null,
    institution: null,
    programme: null,
    outcome: null,
    featured: true,
    consentOnFile: true,
  },
  {
    id: 'aadya-pathak',
    slug: 'aadya-pathak',
    studentName: 'Aadya Pathak',
    posterSrc: '/assets/posters/aadya-pathak.webp',
    photoSrc: null,
    board: 'CBSE Class 10',
    batchYear: '2025-26',
    subjects: [
      { name: 'Science', score: '98' },
      { name: 'Mathematics', score: '96' },
    ],
    metric: null,
    institution: null,
    programme: null,
    outcome: null,
    featured: true,
    consentOnFile: true,
  },
  {
    id: 'arnav-gulati',
    slug: 'arnav-gulati',
    studentName: 'Arnav Gulati',
    posterSrc: '/assets/posters/arnav-gulati.webp',
    photoSrc: '/assets/achievers/arnav-gulati.webp',
    board: 'CBSE Grade 12 · JEE Mains (cleared)',
    batchYear: '2023-24',
    subjects: [
      { name: 'Physics', score: '99/100' },
      { name: 'Maths', score: '97/100' },
    ],
    metric: null,
    institution: null,
    programme: null,
    // Override: two offers, not a single institution/programme pair.
    outcome:
      'National University of Singapore — Computer Science · Imperial College London — AI and Machine Learning',
    featured: true,
    consentOnFile: true,
  },
  {
    id: 'arnav-vivek',
    slug: 'arnav-vivek',
    studentName: 'Arnav Vivek',
    posterSrc: '/assets/posters/arnav-vivek.webp',
    photoSrc: '/assets/achievers/arnav-vivek.webp',
    board: 'PCM',
    batchYear: '2023-24',
    subjects: [],
    metric: null,
    institution: 'IIT Jodhpur',
    programme: 'Computer Science',
    outcome: null,
    featured: true,
    consentOnFile: true,
  },
  {
    id: 'anika-ucl',
    slug: 'anika-ucl',
    studentName: 'Anika [PLACEHOLDER_SURNAME]',
    posterSrc: '/assets/posters/anika-ucl.webp',
    photoSrc: '/assets/achievers/anika-ucl.webp',
    board: 'IBDP',
    batchYear: '2023-24',
    subjects: [
      { name: 'Maths HL', score: '7/7' },
      { name: 'Physics HL', score: '7/7' },
    ],
    metric: null,
    institution: 'University College London',
    programme: 'BSc Economics',
    outcome: null,
    featured: false,
    consentOnFile: true,
  },
  {
    id: 'mihir-kulgod',
    slug: 'mihir-kulgod',
    studentName: 'Mihir Kulgod',
    posterSrc: '/assets/posters/mihir-kulgod.webp',
    photoSrc: '/assets/achievers/mihir-kulgod.webp',
    board: 'Grade 12 — 97.75%',
    batchYear: '2023-24',
    subjects: [
      { name: 'Physics', score: '99' },
      { name: 'Maths', score: '98' },
    ],
    metric: null,
    institution: 'University of California, San Diego',
    programme: null,
    outcome: null,
    featured: false,
    consentOnFile: true,
  },
  {
    id: 'navya-mahendroo',
    slug: 'navya-mahendroo',
    studentName: 'Navya Mahendroo',
    posterSrc: null, // no photograph supplied — no poster generated
    photoSrc: null, // no photo supplied — renders the initials avatar
    board: 'CBSE Grade 12 · Physics & Maths',
    batchYear: '2023-24',
    subjects: [],
    metric: null,
    institution: 'Rutgers University',
    programme: 'Electrical & Computer Engineering',
    outcome: null,
    featured: false,
    consentOnFile: true,
  },
  {
    id: 'krish-bits',
    slug: 'krish-bits',
    studentName: 'Krish [PLACEHOLDER_SURNAME]',
    posterSrc: '/assets/posters/krish-bits.webp',
    photoSrc: '/assets/achievers/krish-bits.webp',
    board: 'Grade 12',
    batchYear: '2023-24',
    subjects: [
      { name: 'Physics', score: '95' },
      { name: 'Maths', score: '95' },
    ],
    metric: null,
    institution: 'BITS Hyderabad',
    programme: 'B.Tech Mechanical Engineering',
    outcome: null,
    featured: false,
    consentOnFile: true,
  },
  {
    id: 'ekkam-singh',
    slug: 'ekkam-singh',
    studentName: 'Ekkam Singh',
    posterSrc: '/assets/posters/ekkam-singh.webp',
    photoSrc: '/assets/achievers/ekkam-singh.webp',
    board: 'JEE Mains',
    batchYear: '2023-24',
    subjects: [],
    metric: null,
    institution: 'Thapar Institute of Technology',
    programme: 'Robotics and AI Engineering',
    outcome: null,
    featured: false,
    consentOnFile: true,
  },
  {
    id: 'priyanka-vinayak',
    slug: 'priyanka-vinayak',
    studentName: 'Priyanka Vinayak',
    posterSrc: '/assets/posters/priyanka-vinayak.webp',
    photoSrc: '/assets/achievers/priyanka-vinayak.webp',
    board: 'Physics, Mathematics',
    batchYear: '2020-21',
    subjects: [],
    metric: null,
    institution: 'NID Ahmedabad',
    programme: null,
    outcome: null,
    featured: false,
    consentOnFile: true,
  },
  {
    id: 'ritisha-kapoor',
    slug: 'ritisha-kapoor',
    studentName: 'Ritisha Kapoor',
    posterSrc: '/assets/posters/ritisha-kapoor.webp',
    photoSrc: '/assets/achievers/ritisha-kapoor.webp',
    board: null,
    batchYear: '2023-24',
    subjects: [],
    metric: null,
    institution: null,
    programme: null,
    // Override: four institutions, not a single institution/programme pair.
    // [VERIFY_MIT_BANGALORE] resolved by owner: Manipal Institute of
    // Technology (Bengaluru) — never abbreviate to "MIT" on this site.
    outcome:
      'VIT Vellore · Manipal Institute of Technology (Bengaluru) · SRM Chennai · Banasthali Jaipur — Computer Science',
    featured: true,
    consentOnFile: true,
  },
  {
    id: 'sneha-ca',
    slug: 'sneha-ca',
    studentName: 'Sneha [PLACEHOLDER_SURNAME]',
    posterSrc: '/assets/posters/sneha-ca.webp',
    photoSrc: '/assets/achievers/sneha-ca.webp',
    board: 'CA Foundation',
    batchYear: '2023-24',
    subjects: [{ name: 'Maths', score: '87' }],
    metric: null,
    institution: null,
    programme: null,
    // Override: no institution/programme shape fits a professional exam clear.
    outcome: 'Cleared CA Foundation',
    featured: false,
    consentOnFile: true,
  },
  {
    id: 'abhinav-ray',
    slug: 'abhinav-ray',
    studentName: 'Abhinav Ray',
    posterSrc: '/assets/posters/abhinav-ray.webp',
    photoSrc: '/assets/achievers/abhinav-ray.webp',
    board: 'JEE Mains',
    batchYear: '[PLACEHOLDER_BATCH]',
    subjects: [],
    metric: '99.14 percentile',
    institution: null,
    programme: null,
    outcome: null, // no admission line — metric carries the story
    featured: true,
    consentOnFile: true,
  },
  {
    id: 'mayan-bhaskar',
    slug: 'mayan-bhaskar',
    studentName: 'Mayan Bhaskar',
    posterSrc: '/assets/posters/mayan-bhaskar.webp',
    photoSrc: '/assets/achievers/mayan-bhaskar.webp',
    board: 'CBSE Class 10',
    batchYear: '2024-25',
    subjects: [
      { name: 'Science', score: '99' },
      { name: 'Maths', score: '100' },
    ],
    metric: null,
    institution: null,
    programme: null,
    outcome: null, // board-result-only record — no admission line
    featured: false,
    consentOnFile: true,
  },
  {
    id: 'shreyash-todi',
    slug: 'shreyash-todi',
    studentName: 'Shreyash Todi',
    posterSrc: '/assets/posters/shreyash-todi.webp',
    photoSrc: '/assets/achievers/shreyash-todi.webp',
    board: 'ICSE Grade 10 — 95.16%',
    batchYear: '2023-24',
    // Subject names only — no marks given on the source poster.
    subjects: [{ name: 'Physics' }, { name: 'Chemistry' }, { name: 'Mathematics' }],
    metric: null,
    institution: null,
    programme: null,
    outcome: null,
    featured: false,
    consentOnFile: true,
  },
  {
    // Deliberately consent-less seed: proves the gate. Must NEVER render.
    id: 'card-placeholder-no-consent',
    slug: 'card-placeholder-no-consent',
    studentName: '[PLACEHOLDER_NO_CONSENT_CARD]',
    photoSrc: null,
    board: '[PLACEHOLDER_BOARD]',
    batchYear: '[PLACEHOLDER_BATCH_YEAR]',
    subjects: [{ name: '[PLACEHOLDER_SUBJECT_1]', score: '[SCORE]' }],
    metric: null,
    institution: null,
    programme: null,
    outcome: '[PLACEHOLDER_OUTCOME]',
    featured: false,
    consentOnFile: false,
  },
];
